import type { Maybe } from '@adornis/base/utilTypes.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import type { EntityPickerOperation } from '@adornis/entity-picker/entity-picker-operation.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { addMinutes, format } from 'date-fns';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { Group } from '../../../groups/entities/Group.js';
import { School } from '../../../groups/entities/School.js';
import { GenerateSlotsForSchoolOptions, InterviewAPI } from '../api/InterviewAPI.js';

const GeneratorOptionsForm = stateful(
  (props: { schoolID: Maybe<string>; resolve: (res: GenerateSlotsForSchoolOptions) => void; reject: () => void }) => {
    const [initialOptions] = useState(new GenerateSlotsForSchoolOptions({ schoolID: props.schoolID }), [
      props.schoolID,
    ]);
    const form = useForm(initialOptions, { validator: GenerateSlotsForSchoolOptions.prototype.validator.functions }, [
      props.schoolID,
    ]);
    const document = form.document;
    const startField = form.field('start');

    const numberOfInterviewsGenerated =
      (document.numberOfSlotsPerTimeSlot ?? 0) * (document.numberOfTimeslotsPerDay ?? 0);

    const start = document.start ?? null;
    const theoreticalEndTime = start
      ? addMinutes(start, (document.numberOfTimeslotsPerDay ?? 0) * (document.numberOfMinutesPerSlot ?? 0))
      : null;

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Termine generieren',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}
        <p>
          Hier können Sie die Termine für die Anmeldegespräche generieren. Die Termine werden fortlaufend ab dem
          Zeitpunkt der Startzeit generiert.
        </p>
        ${
          !props.schoolID
            ? html`
                <div class="flex flex-col gap-2">
                  <p>An welcher Schule sollen die Termine generiert werden?</p>
                  ${EntityPickerInput({
                    ...form.field('schoolID'),
                    placeholder: 'Schule',
                    class: Group,
                    // the API returns NarrowedEntity results, which the picker's nominal entity typing rejects
                    operation: GroupsAPI.subscribeGroups as unknown as EntityPickerOperation<Group, [AdornisFilter]>,
                    filter: new AdornisFilter({
                      aspects: [
                        new StringAspect({ fieldName: '_class', value: School._class, mode: 'equals' }),
                        new BooleanAspect({ fieldName: 'isSgSchool', value: true }),
                      ],
                    }),
                    selectionSet: { name: 1, _id: 1 },
                  })}
                </div>
              `
            : nothing
        }

        <div class="flex flex-col gap-2">
          <p>An welchem Tag und ab welcher Uhrzeit sollen die Termine generiert werden?</p>
          ${DateInput({
            type: 'datetime-local',
            placeholder: 'Startzeit',
            error: startField.error,
            value: document.start,
            onPicked: (picked: Date | null) => startField.onPicked?.(picked),
          })}
        </div>
        <div class="flex flex-col gap-2">
          <p>
            Wie viele Zeitslots sollen pro Tag generiert werden? Ein Zeitslot legt die Zeit für mehrere Termine fest.
          </p>
          ${NumberInput({
            ...form.field('numberOfTimeslotsPerDay'),
            placeholder: 'Zeitslots am Tag',
          })}
        </div>

        <div class="flex flex-col gap-2">
          <p>Wie viele gleichzeitige Termine sollen pro Zeitslot generiert werden?</p>
          ${NumberInput({
            ...form.field('numberOfSlotsPerTimeSlot'),
            placeholder: 'Termine pro Zeitslot',
          })}
        </div>

        <div class="flex flex-col gap-2">
          <p>Wie lange geht ein Termin in Minuten? Es wird keine Pause zwischen den Terminen vorgesehen.</p>
          ${NumberInput({
            ...form.field('numberOfMinutesPerSlot'),
            placeholder: 'Terminlänge in Minuten',
          })}
        </div>

        <p>Start erster Termin: ${start ? format(start, 'HH:mm') : '---'}</p>
        <p>Ende letzter Termin: ${theoreticalEndTime ? format(theoreticalEndTime, 'HH:mm') : '---'}</p>

        <button
          class="btn btn-primary btn-lg ${numberOfInterviewsGenerated === 0 ? 'btn-disabled' : ''}"
          @click=${async () => {
            const errors = await form.validate();
            if (errors) return;
            props.resolve(document);
          }}
        >
          ${numberOfInterviewsGenerated ? `${numberOfInterviewsGenerated} ` : ''}Termine generieren
        </button>
      </div>
    `;
  },
);

export async function handleGenerateInterviewTimeslots(schoolID: Maybe<string>) {
  // ask the user for the specifics
  let generatorOptions: GenerateSlotsForSchoolOptions | null = null;
  try {
    generatorOptions = await showDialog<GenerateSlotsForSchoolOptions>({
      content: (resolve, reject) => GeneratorOptionsForm({ schoolID, resolve, reject }),
    });
  } catch {
    // dialog was dismissed
    return;
  }

  if (!generatorOptions) return;
  const options = generatorOptions;

  await guardDialog(() => InterviewAPI.generate(options)(), {
    success: numberOfGeneratedSlots => {
      void SgSnackbar.show(`Es wurden ${numberOfGeneratedSlots} Termine generiert.`);
    },
  });
}
